/**
 * Client ของหน้าจอจัดการโรงงานและคลัง/ที่เก็บ (`/v1/admin/plants`, `/company-codes`) — D-176
 *
 * หลักการเดียวกับ `lib/ssoAdmin/client.ts`: ฟังก์ชันไม่ throw, payload ผิดรูปคือ
 * `invalid-response`, ไม่เขียนอะไรลง storage (D-049)
 */

import type { CompanyCode, Plant, StorageLocation } from './types';
import { apiFetch } from '../auth/authorizedFetch';
import { buildApiUrl, getConfiguredApiBaseUrl } from '../apiBase';
import type { ApiErrorEnvelope, RbacAdminFailure } from '../rbacAdmin/client';

export type OrgAdminFailure = RbacAdminFailure;

export interface OrgAdminRequestOptions {
  readonly apiBaseUrl?: string;
  readonly fetchImpl?: typeof fetch;
  readonly signal?: AbortSignal;
}

export const PLANTS_PATH = '/v1/admin/plants';
export const COMPANY_CODES_PATH = '/v1/admin/company-codes';

/* `encodeURIComponent` ทุก segment: รหัสเหล่านี้เป็น business key ที่ผู้ดูแลพิมพ์เอง */
export function buildPlantPath(plantCode: string): string {
  return `${PLANTS_PATH}/${encodeURIComponent(plantCode)}`;
}

export function buildLocationsPath(plantCode: string): string {
  return `${buildPlantPath(plantCode)}/storage-locations`;
}

export function buildLocationPath(plantCode: string, locationCode: string): string {
  return `${buildLocationsPath(plantCode)}/${encodeURIComponent(locationCode)}`;
}

export type CompanyCodeListResult =
  | { readonly status: 'ready'; readonly items: readonly CompanyCode[] }
  | OrgAdminFailure;
export type PlantListResult =
  | { readonly status: 'ready'; readonly items: readonly Plant[] }
  | OrgAdminFailure;
export type PlantResult = { readonly status: 'ready'; readonly plant: Plant } | OrgAdminFailure;
export type LocationListResult =
  | { readonly status: 'ready'; readonly items: readonly StorageLocation[] }
  | OrgAdminFailure;
export type LocationResult =
  | { readonly status: 'ready'; readonly location: StorageLocation }
  | OrgAdminFailure;

/* -------------------------------------------------------------- parsing -- */

function asRecord(raw: unknown): Record<string, unknown> | null {
  return typeof raw === 'object' && raw !== null && !Array.isArray(raw)
    ? (raw as Record<string, unknown>)
    : null;
}

function str(value: unknown): string | null {
  return typeof value === 'string' && value.trim() !== '' ? value : null;
}

export function parseCompanyCode(raw: unknown): CompanyCode | null {
  const r = asRecord(raw);
  const code = str(r?.company_code);
  const name = str(r?.name);
  return code === null || name === null ? null : { company_code: code, name };
}

export function parsePlant(raw: unknown): Plant | null {
  const r = asRecord(raw);
  if (r === null) {
    return null;
  }
  const plantCode = str(r.plant_code);
  const name = str(r.name);
  const company = str(r.company_code);
  const created = str(r.created_at);
  const updated = str(r.updated_at);
  const count = r.storage_location_count;
  if (
    plantCode === null ||
    name === null ||
    company === null ||
    created === null ||
    updated === null ||
    typeof count !== 'number' ||
    !Number.isInteger(count) ||
    count < 0
  ) {
    return null;
  }
  return {
    plant_code: plantCode,
    name,
    company_code: company,
    storage_location_count: count,
    created_at: created,
    updated_at: updated,
  };
}

export function parseLocation(raw: unknown): StorageLocation | null {
  const r = asRecord(raw);
  if (r === null) {
    return null;
  }
  const plantCode = str(r.plant_code);
  const code = str(r.storage_location_code);
  const name = str(r.name);
  const type = str(r.location_type);
  const created = str(r.created_at);
  const updated = str(r.updated_at);
  if (
    plantCode === null ||
    code === null ||
    name === null ||
    type === null ||
    created === null ||
    updated === null
  ) {
    return null;
  }
  return {
    plant_code: plantCode,
    storage_location_code: code,
    name,
    location_type: type,
    created_at: created,
    updated_at: updated,
  };
}

function parseItems<T>(raw: unknown, parseOne: (entry: unknown) => T | null): T[] | null {
  const r = asRecord(raw);
  if (r === null || !Array.isArray(r.items)) {
    return null;
  }
  const out: T[] = [];
  for (const entry of r.items) {
    const parsed = parseOne(entry);
    if (parsed === null) {
      return null;
    }
    out.push(parsed);
  }
  return out;
}

function parseErrorEnvelope(raw: unknown): ApiErrorEnvelope {
  const r = asRecord(raw);
  if (r === null) {
    return { error_code: null, message: null, rule: null, details: {} };
  }
  const details = asRecord(r.details) ?? {};
  return {
    error_code: str(r.error_code),
    message: str(r.message),
    rule: str(details.rule),
    details,
  };
}

/* ------------------------------------------------------------- requests -- */

async function readEnvelope(response: Response): Promise<ApiErrorEnvelope> {
  try {
    return parseErrorEnvelope(await response.json());
  } catch {
    return parseErrorEnvelope(null);
  }
}

type PayloadResult = { readonly status: 'payload'; readonly payload: unknown } | OrgAdminFailure;

async function requestPayload(
  path: string,
  init: RequestInit,
  options: OrgAdminRequestOptions,
): Promise<PayloadResult> {
  const fetchImpl = options.fetchImpl ?? apiFetch;
  if (typeof fetchImpl !== 'function') {
    return { status: 'network-error', detail: 'fetch ไม่พร้อมใช้งานใน runtime นี้' };
  }

  const url = buildApiUrl(options.apiBaseUrl ?? getConfiguredApiBaseUrl(), path);

  let response: Response;
  try {
    response = await fetchImpl(url, {
      credentials: 'same-origin',
      cache: 'no-store',
      ...init,
      ...(options.signal ? { signal: options.signal } : {}),
    });
  } catch (error) {
    if (options.signal?.aborted || (error as Error | undefined)?.name === 'AbortError') {
      return { status: 'cancelled' };
    }
    return {
      status: 'network-error',
      detail: error instanceof Error ? error.message : 'ไม่ทราบสาเหตุ',
    };
  }

  if (options.signal?.aborted) {
    return { status: 'cancelled' };
  }

  switch (response.status) {
    case 400:
      return { status: 'bad-request', error: await readEnvelope(response) };
    case 401:
      return { status: 'unauthenticated' };
    case 403:
      return { status: 'forbidden', error: await readEnvelope(response) };
    case 404:
      return { status: 'not-found', error: await readEnvelope(response) };
    case 409:
      return { status: 'conflict', error: await readEnvelope(response) };
    case 422:
      return { status: 'rule-violation', error: await readEnvelope(response) };
    default:
      break;
  }

  if (!response.ok) {
    return { status: 'server-error', httpStatus: response.status };
  }
  try {
    return { status: 'payload', payload: await response.json() };
  } catch {
    return { status: 'invalid-response' };
  }
}

const READ_INIT: RequestInit = { method: 'GET', headers: { Accept: 'application/json' } };

function jsonInit(method: string, body: unknown): RequestInit {
  return {
    method,
    headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  };
}

async function readList<T>(
  path: string,
  parseOne: (entry: unknown) => T | null,
  options: OrgAdminRequestOptions,
): Promise<{ readonly status: 'ready'; readonly items: readonly T[] } | OrgAdminFailure> {
  const result = await requestPayload(path, READ_INIT, options);
  if (result.status !== 'payload') {
    return result;
  }
  const items = parseItems(result.payload, parseOne);
  return items === null ? { status: 'invalid-response' } : { status: 'ready', items };
}

export function fetchCompanyCodes(
  options: OrgAdminRequestOptions = {},
): Promise<CompanyCodeListResult> {
  return readList(COMPANY_CODES_PATH, parseCompanyCode, options);
}

export function fetchPlants(options: OrgAdminRequestOptions = {}): Promise<PlantListResult> {
  return readList(PLANTS_PATH, parsePlant, options);
}

async function plantResult(
  path: string,
  init: RequestInit,
  options: OrgAdminRequestOptions,
): Promise<PlantResult> {
  const result = await requestPayload(path, init, options);
  if (result.status !== 'payload') {
    return result;
  }
  const plant = parsePlant(result.payload);
  return plant === null ? { status: 'invalid-response' } : { status: 'ready', plant };
}

/* body ส่งเฉพาะคีย์ที่ schema รับ (`extra = "forbid"`) ไม่ spread object ทั้งก้อน */
export function createPlant(
  body: { readonly plant_code: string; readonly name: string; readonly company_code: string },
  options: OrgAdminRequestOptions = {},
): Promise<PlantResult> {
  return plantResult(
    PLANTS_PATH,
    jsonInit('POST', {
      plant_code: body.plant_code,
      name: body.name,
      company_code: body.company_code,
    }),
    options,
  );
}

export function renamePlant(
  plantCode: string,
  name: string,
  options: OrgAdminRequestOptions = {},
): Promise<PlantResult> {
  return plantResult(buildPlantPath(plantCode), jsonInit('PATCH', { name }), options);
}

export function fetchLocations(
  plantCode: string,
  options: OrgAdminRequestOptions = {},
): Promise<LocationListResult> {
  return readList(buildLocationsPath(plantCode), parseLocation, options);
}

async function locationResult(
  path: string,
  init: RequestInit,
  options: OrgAdminRequestOptions,
): Promise<LocationResult> {
  const result = await requestPayload(path, init, options);
  if (result.status !== 'payload') {
    return result;
  }
  const location = parseLocation(result.payload);
  return location === null ? { status: 'invalid-response' } : { status: 'ready', location };
}

export function createLocation(
  plantCode: string,
  body: {
    readonly storage_location_code: string;
    readonly name: string;
    readonly location_type: string;
  },
  options: OrgAdminRequestOptions = {},
): Promise<LocationResult> {
  return locationResult(
    buildLocationsPath(plantCode),
    jsonInit('POST', {
      storage_location_code: body.storage_location_code,
      name: body.name,
      location_type: body.location_type,
    }),
    options,
  );
}

export function renameLocation(
  plantCode: string,
  locationCode: string,
  name: string,
  options: OrgAdminRequestOptions = {},
): Promise<LocationResult> {
  return locationResult(
    buildLocationPath(plantCode, locationCode),
    jsonInit('PATCH', { name }),
    options,
  );
}
